import { useEffect, useMemo, useState } from 'react';
import type {
  EntityReconcileCandidate,
  EntityReconcileScoredResponse,
} from 'types/api-responses';
import { fetchScoredCandidates } from 'pages/entities/show/reconcile/api';
import {
  candidateRowId,
  scoredCandidate,
} from 'pages/entities/show/reconcile/reconcile-candidate-helpers';
import {
  FIRST_SUGGESTION_LABEL,
  USER_CREATED_LABEL,
  USER_SELECTED_LABEL,
} from '../utils';
import type { ReconcileCandidate } from './types';

export interface MatchReviewSession {
  // The anchor as of the open, which the session's rows were loaded for.
  anchorId: number;
  anchorType: ReconcileCandidate['type'];
  response: EntityReconcileScoredResponse;
  live: EntityReconcileCandidate[];
  rows: ReconcileCandidate[];
  preCheckIds: number[];
}

export type MatchReviewReconcile =
  | { status: 'idle' | 'loading' | 'failed' | 'empty' | 'no-anchor' }
  | { status: 'ready'; session: MatchReviewSession };

type Fetched =
  | { anchorId: number; response: EntityReconcileScoredResponse }
  | { anchorId: number; failed: true };

const USER_ROW_LABELS = [USER_SELECTED_LABEL, USER_CREATED_LABEL];

/**
 * Live rows are keyed by their display member; a persisted row may carry either
 * the family canonical or the display member, so both resolve to the live row.
 * @param {EntityReconcileCandidate[]} live - The live scored candidates.
 * @return {Map<number, number>} Each canonical or display id to its row id.
 */
const liveRowIds = (live: EntityReconcileCandidate[]): Map<number, number> => {
  const ids = new Map<number, number>();
  live.forEach((c) => {
    const rowId = candidateRowId(c);
    ids.set(c.entityId, rowId);
    ids.set(rowId, rowId);
  });
  return ids;
};

/**
 * Merges the live scored candidates with the party's persisted rows: the anchor,
 * the live rows, then user rows the live list lacks.
 * @param {number} anchorId - The anchor as of the open.
 * @param {EntityReconcileScoredResponse} response - The scored candidates.
 * @param {ReconcileCandidate[]} persisted - The party's rows as of the open.
 * @return {MatchReviewSession | null} The session, or null when nothing is left
 *   to reconcile.
 */
const buildSession = (
  anchorId: number,
  response: EntityReconcileScoredResponse,
  persisted: ReconcileCandidate[]
): MatchReviewSession | null => {
  const anchor = persisted.find((c) => c.isStaged);
  const live = response.allEliminated
    ? []
    : (response.candidates ?? []).filter(
        (c) =>
          c.entityId !== anchorId && c.aliasGroup?.displayEntityId !== anchorId
      );
  const rowIdFor = liveRowIds(live);
  const userRows = persisted.filter(
    (c) =>
      !c.isStaged &&
      USER_ROW_LABELS.includes(c.sourceLabel) &&
      !rowIdFor.has(c.id)
  );
  if (live.length === 0 && userRows.length === 0) return null;

  // §3.2 pre-check: the persisted Suggestion 1 when the live list still has it
  // (its name becomes the initial survivor), then the anchor.
  const suggestionOne = persisted.find(
    (c) => c.sourceLabel === FIRST_SUGGESTION_LABEL
  );
  const suggestionOneRowId =
    suggestionOne && !suggestionOne.flagged
      ? rowIdFor.get(suggestionOne.id)
      : undefined;
  const preCheckIds = [
    ...(suggestionOneRowId !== undefined ? [suggestionOneRowId] : []),
    ...(anchor && !anchor.flagged ? [anchor.id] : []),
  ];

  return {
    anchorId,
    anchorType: anchor?.type ?? 'ImportedEntity',
    response,
    live,
    rows: [
      ...(anchor ? [anchor] : []),
      ...live.map(scoredCandidate),
      ...userRows,
    ],
    preCheckIds,
  };
};

/**
 * Loads the live scored candidates (the endpoint the entity page uses) once per
 * open for the anchor captured at the open, and holds them with the party's
 * persisted rows as of the same open, so a party-detail refetch never resets the
 * examiner's consolidation. The reconcile submit carries the opened anchor, and
 * the server refuses it once the party has moved to another. Both are released
 * on close.
 * @param {boolean} open - Whether the tearsheet is open.
 * @param {number | null} anchorId - The party's current anchor, read at the open.
 * @param {ReconcileCandidate[]} candidates - The party's persisted rows.
 * @return {MatchReviewReconcile} The load status and, once ready, the session.
 */
export function useMatchReviewReconcile(
  open: boolean,
  anchorId: number | null,
  candidates: ReconcileCandidate[]
): MatchReviewReconcile {
  const [opened, setOpened] = useState<{
    anchorId: number | null;
    persisted: ReconcileCandidate[];
  } | null>(null);
  const [fetched, setFetched] = useState<Fetched | null>(null);
  if (open && opened === null) setOpened({ anchorId, persisted: candidates });
  if (!open && opened !== null) {
    setOpened(null);
    setFetched(null);
  }
  const openedAnchorId = opened ? opened.anchorId : anchorId;
  const persisted = opened?.persisted ?? null;

  useEffect(() => {
    if (!open || openedAnchorId === null) return undefined;
    let cancelled = false;
    fetchScoredCandidates(openedAnchorId)
      .then((response) => {
        if (!cancelled) setFetched({ anchorId: openedAnchorId, response });
      })
      .catch(() => {
        if (!cancelled) setFetched({ anchorId: openedAnchorId, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [open, openedAnchorId]);

  const current = fetched?.anchorId === openedAnchorId ? fetched : null;
  const session = useMemo(() => {
    if (!current || !('response' in current) || !persisted) return null;
    return buildSession(current.anchorId, current.response, persisted);
  }, [current, persisted]);

  if (!open) return { status: 'idle' };
  if (openedAnchorId === null) return { status: 'no-anchor' };
  if (!current) return { status: 'loading' };
  if ('failed' in current) return { status: 'failed' };
  return session ? { status: 'ready', session } : { status: 'empty' };
}
